import * as Sentry from '@sentry/react';

// ===============================
// SENTRY — Error Monitoring
// Inicializar antes del render de React.
// DSN viene de .env como VITE_SENTRY_DSN.
// ===============================

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENV = import.meta.env.MODE; // 'development' | 'production'

// No inicializar Sentry en local si no hay DSN configurado
if (!SENTRY_DSN) {
  console.warn('[Sentry] VITE_SENTRY_DSN no está definido. Sentry desactivado.');
}

// Guard anti doble-init: si el módulo se evalúa dos veces (HMR de Vite en dev,
// o import duplicado), Sentry.init vuelve a instanciar replayIntegration y tira
// "Multiple Sentry Session Replay instances are not supported". getClient()
// devuelve un cliente activo tras la primera init, así que evitamos la segunda.
if (!Sentry.getClient()) {
Sentry.init({
  dsn: SENTRY_DSN,

  // Ambiente: 'development' o 'production'
  environment: ENV,

  // Sólo habilitar en producción para no saturar de errores de desarrollo
  enabled: !!SENTRY_DSN,

  // ---------------------------------------------------------------
  // PERFORMANCE MONITORING
  // tracesSampleRate: 1.0 = 100% de transacciones (reducir en prod
  // si el volumen es alto, ej: 0.1 para 10%)
  // ---------------------------------------------------------------
  tracesSampleRate: ENV === 'production' ? 0.2 : 1.0,

  // ---------------------------------------------------------------
  // SESSION REPLAY
  // Graba sesiones del usuario cuando ocurre un error.
  // replaysSessionSampleRate: porcentaje de sesiones normales.
  // replaysOnErrorSampleRate: porcentaje de sesiones con error.
  // ---------------------------------------------------------------
  replaysSessionSampleRate: 0.05,  // 5% de sesiones normales
  replaysOnErrorSampleRate: 1.0,   // 100% de sesiones con error

  integrations: [
    // Trazas de navegación y peticiones HTTP
    Sentry.browserTracingIntegration(),

    // Session Replay para ver grabaciones cuando hay errores
    Sentry.replayIntegration({
      // Enmascarar textos e inputs sensibles por defecto
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  // ---------------------------------------------------------------
  // FILTROS — evitar ruido en el dashboard
  // ---------------------------------------------------------------
  ignoreErrors: [
    // Errores de red comunes sin impacto real
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    // Errores de extensiones del browser
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    // React 19 + extensiones (Grammarly, traductores, etc.) que modifican el DOM
    // por fuera de React. El ErrorBoundary los captura y muestra fallback, no
    // rompe la UX. Sentry los reportaba como ruido (ref IMPULZA-1, IMPULZA-2).
    "Failed to execute 'removeChild' on 'Node'",
    "Failed to execute 'insertBefore' on 'Node'",
    "Failed to execute 'appendChild' on 'Node'",
    // Chunk lazy servido como text/html tras un deploy (index.html cacheado
    // referencia hashes viejos). NO es un bug: ya lo auto-recuperamos con
    // hard reload en lazyWithRetry + watcher global en main.jsx. Cuando el
    // reload no alcanza, el ErrorBoundary lo reportaba como ruido (ref IMPULZA-5).
    'is not a valid JavaScript MIME type',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
    'Expected a JavaScript',
    'Loading chunk',
    'Loading CSS chunk',
    // Navegadores in-app (Instagram/Facebook/TikTok) inyectan su propio puente
    // nativo (window.webkit.messageHandlers) vía sendDataToNative /
    // sendPageHideMessage. En el navegador in-app de iOS ese puente no existe y
    // tira "undefined is not an object (evaluating 'window.webkit.messageHandlers')".
    // No es código nuestro ni un bug de la app: es ruido de terceros (ref IMPULZA-7).
    'messageHandlers',
    'sendDataToNative',
    'sendPageHideMessage',
    // Mismo caso del lado Android: el WebView de los navegadores in-app
    // (Instagram/Facebook/TikTok) expone un puente Java y, cuando la vista se
    // destruye o cambia de pestana, su propio postMessage falla con
    // "Java object is gone" / "Java exception was raised during method
    // invocation". No hay postMessage propio en src/ — es ruido de terceros
    // 100% (ref IMPULZA-A, IMPULZA-C).
    'Error invoking postMessage',
    'Java object is gone',
    'Java exception was raised',
  ],

  beforeSend(event) {
    // Trafico automatizado (e2e de Playwright, crawlers) no es un usuario real:
    // ensuciaba el feed con warnings de onboarding disparados por HeadlessChrome
    // (ref GASTIFY-E, GASTIFY-F).
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      const isBot = (typeof navigator !== 'undefined' && navigator.webdriver)
        || /Headless|Playwright|Puppeteer|bot|crawler|spider/i.test(ua);
      if (isBot) return null;
    } catch {
      // navigator no disponible: seguimos normalmente.
    }

    // En development, loguear en consola pero no enviar a Sentry
    if (ENV === 'development') {
      console.group('[Sentry] Error capturado (dev — no enviado)');
      console.error(event);
      console.groupEnd();
      return null; // null = no enviar
    }
    return event;
  },

  beforeSendTransaction(event) {
    // beforeSend solo filtra errores; las transacciones de performance
    // (incluidos los detectores N+1) lo saltan. En development eso hacía que
    // el N+1 de la propia máquina del dev llegara a Sentry (ref IMPULZA-4).
    // Descartamos toda transacción de development.
    if (ENV === 'development') return null;
    return event;
  },
});
}

// ===============================
// reportHandledError — reportar errores "manejados" a Sentry
// -------------------------------------------------------------------
// Sin esto, un catch que solo hace console.error + toast.error queda
// INVISIBLE en Sentry: el dashboard solo ve crashes no manejados
// (ErrorBoundary / window.onerror). Esta helper envía a Sentry los
// errores que ya mostramos al usuario, con contexto (módulo, código de
// error de Supabase/PostgREST, datos extra) para poder diagnosticarlos.
//
// Respeta el comportamiento dev/prod: en development, beforeSend
// devuelve null, así que esto loguea pero no envía. Los errores de red
// (offline / VPN caída en kioscos) siguen filtrados por ignoreErrors,
// para no saturar el dashboard con ruido de conexión.
//
// Uso:
//   import { reportHandledError } from '../utils/sentry';
//   catch (error) {
//     reportHandledError(error, { feature: 'ventas:historial' });
//     toast.error('Error al sincronizar historial');
//   }
//
// @param {unknown} error              Error capturado en el catch.
// @param {object}  [context]
// @param {string}  [context.feature]  Módulo/acción, ej: 'ventas:historial'.
// @param {object}  [context.extra]    Datos extra (ids, filtros activos, etc.).
export function reportHandledError(error, context = {}) {
  const { feature, extra } = context;
  try {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('handled', 'true');
      if (feature) scope.setTag('feature', feature);
      // Código/detalle de error de Supabase/PostgREST si vienen.
      if (error && typeof error === 'object') {
        if (error.code) scope.setTag('error_code', String(error.code));
        if (error.details) scope.setExtra('details', error.details);
        if (error.hint) scope.setExtra('hint', error.hint);
      }
      if (extra && typeof extra === 'object') scope.setExtras(extra);

      // captureException rinde mejor con instancias de Error (stack + mensaje).
      // Los errores de Supabase suelen ser objetos planos: los envolvemos
      // preservando el mensaje original.
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        const message =
          typeof error === 'string'
            ? error
            : error?.message || 'Error manejado sin mensaje';
        Sentry.captureException(new Error(message));
      }
    });
  } catch (e) {
    // Nunca dejar que el reporte a Sentry rompa el flujo de la app.
    console.error('[Sentry] reportHandledError falló:', e);
  }
}

export default Sentry;
